import React, { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const ICONS = {
  dashboard: (<><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /></>),
  sheet: (<><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" /></>),
  sheets: (<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>),
  expenses: (<><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /></>),
  employees: (<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" /></>),
  payment: (<><path d="M3 7h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" /><path d="M3 7V6a2 2 0 0 1 2-2h12" /><circle cx="16.5" cy="13" r="1.2" /></>),
  submit: (<><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>),
  submissions: (<><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></>),
  profile: (<><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>),
};

const Icon = ({ name }) => (
  <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {ICONS[name]}
  </svg>
);

const ADMIN_LINKS = [
  { to: '/admin', label: 'Dashboard', icon: 'dashboard', end: true },
  { to: '/admin/expense-sheet', label: 'Expense Sheet', icon: 'sheet' },
  { to: '/admin/employee-sheets', label: 'Employee Sheets', icon: 'sheets' },
  { to: '/admin/expenses', label: 'All Expenses', icon: 'expenses' },
  { to: '/admin/employees', label: 'Employees', icon: 'employees' },
  { to: '/admin/payment-entry', label: 'Payment Entry', icon: 'payment' },
];

const EMPLOYEE_LINKS = [
  { to: '/employee-dashboard', label: 'Dashboard', icon: 'dashboard' },
  { to: '/submit-expense', label: 'Submit Expense', icon: 'submit' },
  { to: '/my-submissions', label: 'My Submissions', icon: 'submissions' },
  { to: '/expenses', label: 'My Expenses', icon: 'expenses' },
  { to: '/profile', label: 'Profile', icon: 'profile' },
];

const SUBADMIN_LINKS = [
  { to: '/admin/employee-sheets', label: 'Employee Sheets', icon: 'sheets' },
  { to: '/admin/payment-entry', label: 'Payment Entry', icon: 'payment' },
];

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const isAdmin = user?.role === 'admin';
  const isSubAdmin = user?.subAdmin === true;

  const links = isAdmin
    ? ADMIN_LINKS
    : (isSubAdmin ? [...EMPLOYEE_LINKS, ...SUBADMIN_LINKS] : EMPLOYEE_LINKS);

  const handleLogout = () => {
    setOpen(false);
    logout();
    navigate('/login');
  };

  const getInitials = (name) => (name || 'U').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
  const linkClass = ({ isActive }) => `nav-link${isActive ? ' active' : ''}`;

  return (
    <nav className="navbar">
      <div className="navbar-inner">
        <Link
          to={isAdmin ? '/admin' : '/employee-dashboard'}
          className="navbar-brand"
          onClick={() => setOpen(false)}
        >
          <span className="brand-logo">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </span>
          <span className="brand-text">Expense<span>Tracker</span></span>
        </Link>

        <button
          className="nav-toggle"
          aria-label="Toggle navigation"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          {open ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
          )}
        </button>

        <div className={`navbar-menu${open ? ' open' : ''}`}>
          <div className="navbar-nav">
            {links.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} className={linkClass} onClick={() => setOpen(false)}>
                <Icon name={l.icon} />
                <span>{l.label}</span>
              </NavLink>
            ))}
          </div>

          <div className="nav-user">
            <div className="user-avatar">{getInitials(user?.name)}</div>
            <div className="user-info">
              <span className="user-name">{user?.name}</span>
              <span className="user-role">{isSubAdmin ? 'sub-admin' : user?.role}</span>
            </div>
            <button onClick={handleLogout} className="btn btn-sm nav-logout">Logout</button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
